import React, { useEffect, useState } from 'react';
import { Trophy, ChevronLeft, ChevronRight, RefreshCcw, Cpu, BookOpenCheck } from 'lucide-react';

const QUESTIONS = [
  {
    question: 'Which OOP principle allows using a common interface for different underlying forms?',
    options: ['Encapsulation', 'Inheritance', 'Polymorphism', 'Abstraction'],
    answer: 2,
    explanation: 'Polymorphism lets objects be treated as instances of their parent type with different implementations.'
  },
  {
    question: 'What best describes encapsulation?',
    options: ['Combining code and data', 'Hiding internal state', 'Sharing behavior', 'Separating responsibilities'],
    answer: 1,
    explanation: 'Encapsulation hides internal state and requires all interaction to be performed through methods.'
  },
  {
    question: 'A class extends another class. What is this?',
    options: ['Composition', 'Aggregation', 'Inheritance', 'Polymorphism'],
    answer: 2,
    explanation: 'Inheritance allows a class to derive properties and behaviors of another class.'
  },
  {
    question: 'Which keyword prevents a class from being subclassed in Java?',
    options: ['final', 'static', 'abstract', 'sealed'],
    answer: 0,
    explanation: 'In Java, final on a class prevents inheritance.'
  },
  {
    question: 'What is abstraction in OOP?',
    options: ['Detail exposure', 'Hiding complexity', 'Data duplication', 'Runtime binding'],
    answer: 1,
    explanation: 'Abstraction hides complex implementation details and exposes simpler interfaces.'
  },
  {
    question: 'Which is true about interfaces in Java?',
    options: ['Can be instantiated', 'Can contain state only', 'Define contracts with methods', 'Must be final'],
    answer: 2,
    explanation: 'Interfaces declare contracts; classes implement them to provide behavior.'
  },
  {
    question: 'Method overloading means…',
    options: ['Same name, different parameters', 'Same name, same parameters', 'Different name, same parameters', 'Redefining method in subclass'],
    answer: 0,
    explanation: 'Overloading uses the same method name with different parameter lists in the same class.'
  },
  {
    question: 'Method overriding means…',
    options: ['Redefining a method in a subclass', 'Changing access modifier only', 'Same signature in same class', 'Adding new methods'],
    answer: 0,
    explanation: 'Overriding replaces a superclass method implementation in a subclass with the same signature.'
  },
  {
    question: 'Which is NOT an OOP principle?',
    options: ['Encapsulation', 'Abstraction', 'Serialization', 'Polymorphism'],
    answer: 2,
    explanation: 'Serialization is a persistence mechanism, not a core OOP principle.'
  },
  {
    question: 'In Java, which enables multiple inheritance of type?',
    options: ['Classes', 'Interfaces', 'Enums', 'Records'],
    answer: 1,
    explanation: 'Java allows multiple inheritance via interfaces; classes support single inheritance.'
  },
  {
    question: 'What is composition?',
    options: ['IS-A relationship', 'HAS-A relationship with ownership', 'Indirect association only', 'Static binding of behavior'],
    answer: 1,
    explanation: 'Composition models a HAS-A relationship where a class owns the lifecycle of its parts.'
  },
  {
    question: 'Which access modifier makes members visible only within the same class?',
    options: ['public', 'protected', 'private', 'package-private'],
    answer: 2,
    explanation: 'private restricts visibility to the declaring class.'
  },
  {
    question: 'Dynamic dispatch refers to…',
    options: ['Compile-time binding', 'Runtime method selection', 'Field resolution', 'Static linking'],
    answer: 1,
    explanation: 'Dynamic dispatch selects which overridden method to call at runtime based on the object.'
  },
  {
    question: 'What is an abstract class?',
    options: ['A class with no constructor', 'A class that cannot be instantiated', 'A class without fields', 'A final class'],
    answer: 1,
    explanation: 'Abstract classes may contain abstract methods and cannot be instantiated directly.'
  },
  {
    question: 'Which keyword is used to call a superclass constructor in Java?',
    options: ['this', 'super', 'base', 'parent'],
    answer: 1,
    explanation: 'super(...) calls the superclass constructor.'
  },
  {
    question: 'Which statement is true about getters/setters?',
    options: ['They break encapsulation', 'They enforce encapsulation', 'They cause polymorphism', 'They enable inheritance'],
    answer: 1,
    explanation: 'Accessors control how state is read or mutated, supporting encapsulation.'
  },
  {
    question: 'Liskov Substitution Principle implies…',
    options: ['Subclasses must be faster', 'Subtypes can substitute supertypes without breaking correctness', 'No overriding allowed', 'Only composition allowed'],
    answer: 1,
    explanation: 'Objects of a superclass should be replaceable with objects of a subclass without altering desirable properties.'
  },
  {
    question: 'Which is true about sealed classes (Java 17+)?',
    options: ['Cannot have subclasses', 'Must specify permitted subclasses', 'Are identical to final classes', 'Must be interfaces'],
    answer: 1,
    explanation: 'Sealed classes restrict which classes may extend them via a permits list.'
  },
  {
    question: 'Overriding a method and calling super.method() achieves…',
    options: ['Hiding', 'Ignoring base behavior', 'Extending base behavior', 'Shadowing fields only'],
    answer: 2,
    explanation: 'You can extend behavior by calling the base implementation before/after custom logic.'
  },
  {
    question: 'What is aggregation?',
    options: ['Whole-part with shared lifecycle', 'Whole-part with independent lifecycle', 'IS-A relation', 'Static binding'],
    answer: 1,
    explanation: 'Aggregation is a HAS-A relation where parts can outlive the whole (no ownership).'
  }
];

export default function App() {
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [glitch, setGlitch] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setGlitch((g) => !g), 2500);
    return () => clearInterval(t);
  }, []);

  const q = QUESTIONS[current];

  const handleSelect = (idx) => {
    if (showResult) return;
    setSelected(idx);
  };

  const handleNext = () => {
    if (!showResult && selected !== null) {
      const correct = selected === q.answer;
      setScore((s) => s + (correct ? 1 : 0));
    }
    setShowResult(false);
    setSelected(null);
    setCurrent((c) => Math.min(c + 1, QUESTIONS.length - 1));
  };

  const handlePrev = () => {
    setShowResult(false);
    setSelected(null);
    setCurrent((c) => Math.max(c - 1, 0));
  };

  const reveal = () => {
    if (selected === null) return;
    setShowResult(true);
  };

  const reset = () => {
    setCurrent(0);
    setSelected(null);
    setShowResult(false);
    setScore(0);
  };

  const progress = Math.round(((current) / (QUESTIONS.length - 1)) * 100);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black">
      <div className="crt relative w-[95%] max-w-4xl mx-auto border border-emerald-500 rounded-xl overflow-hidden">
        {/* Header */}
        <div className="p-4 sm:p-6 bg-[#05120d] border-b border-emerald-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Cpu size={22} className="text-emerald-400" />
              <h1 className={`text-emerald-300 text-lg sm:text-2xl font-semibold glow ${glitch ? 'opacity-95' : 'opacity-100'}`}>THEORETICAL OOP CONSOLE</h1>
            </div>
            <div className="flex items-center gap-3 text-emerald-300">
              <Trophy size={20} />
              <span className="text-sm sm:text-base">Score: {score}/{QUESTIONS.length}</span>
            </div>
          </div>
          <div className="mt-3 h-1.5 bg-emerald-900 rounded">
            <div className="h-full bg-emerald-400" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Question */}
          <div className="lg:col-span-2">
            <div className="border border-emerald-700 rounded-lg p-4 bg-[#07140f]">
              <div className="flex items-center gap-2 text-emerald-300 mb-2">
                <BookOpenCheck size={18} />
                <span className="text-sm">Question {current + 1} of {QUESTIONS.length}</span>
              </div>
              <p className="text-emerald-200 text-base sm:text-lg leading-relaxed">
                {q.question}
              </p>
            </div>

            {/* Options */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {q.options.map((opt, idx) => {
                const isSelected = selected === idx;
                const isCorrect = idx === q.answer;
                let classes = 'btn border rounded-lg p-3 text-left bg-[#08110c]';
                let border = 'border-emerald-700';
                let text = 'text-emerald-300';

                if (showResult && isSelected && isCorrect) {
                  border = 'border-emerald-400';
                  text = 'text-emerald-200';
                  classes += ' shadow-[0_0_20px_rgba(43,255,115,0.25)]';
                } else if (showResult && isSelected && !isCorrect) {
                  border = 'border-red-500';
                  text = 'text-red-300';
                } else if (!showResult && isSelected) {
                  border = 'border-emerald-500';
                  text = 'text-emerald-200';
                }

                return (
                  <button
                    key={idx}
                    onClick={() => handleSelect(idx)}
                    className={`${classes} ${border} ${text}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-emerald-500">[{String.fromCharCode(65 + idx)}]</span>
                      <span>{opt}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Controls */}
            <div className="mt-4 flex items-center gap-3">
              <button onClick={handlePrev} className="btn px-3 py-2 border border-emerald-700 text-emerald-300 rounded flex items-center gap-2 bg-[#08110c]">
                <ChevronLeft size={18} /> Prev
              </button>
              <button onClick={reveal} disabled={selected === null} className="btn px-3 py-2 border border-emerald-700 text-emerald-300 rounded bg-[#08110c] disabled:opacity-40">
                Reveal
              </button>
              <button onClick={handleNext} className="btn px-3 py-2 border border-emerald-700 text-emerald-300 rounded flex items-center gap-2 bg-[#08110c]">
                Next <ChevronRight size={18} />
              </button>
              <button onClick={reset} className="ml-auto btn px-3 py-2 border border-emerald-700 text-emerald-300 rounded flex items-center gap-2 bg-[#08110c]">
                <RefreshCcw size={18} /> Reset
              </button>
            </div>
          </div>

          {/* Feedback */}
          <div>
            <div className="border border-emerald-700 rounded-lg p-4 bg-[#07140f]">
              <h3 className="text-emerald-300 font-semibold mb-2">Feedback</h3>
              {selected === null && (
                <p className="text-emerald-500">Select an option to continue.</p>
              )}
              {selected !== null && !showResult && (
                <p className="text-emerald-400">Press Reveal to check the answer.</p>
              )}
              {selected !== null && showResult && (
                <div className="space-y-2">
                  {selected === q.answer ? (
                    <p className="text-emerald-300">Correct ✅</p>
                  ) : (
                    <p className="text-red-400">Incorrect ❌</p>
                  )}
                  <p className="text-emerald-200">Correct Answer: <span className="text-emerald-400">{q.options[q.answer]}</span></p>
                  <p className="text-emerald-300">Reason: {q.explanation}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-[#05120d] border-t border-emerald-700 text-emerald-300 text-xs sm:text-sm">
          <div className="flex items-center justify-between">
            <span>CORE_OOP: ACTIVE • CRT_LINES: STABLE • SIGNAL: {glitch ? 'NOISY' : 'CLEAR'}</span>
            <span>THEORETICAL × JAVA</span>
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="mt-3 text-emerald-600 text-xs">
        Press Reveal before Next to record your answer.
      </div>
    </div>
  );
}
